<template>
  <div class="right-open-issues">
    <div class="right-open-issues-filters">
      <div class="custom-row">
        <div class="open-issues-select">
          <select class="v1-selectbox" id="openIssuesSelectBox" v-model="selectedFilter" @change="applyFilters">
            <option value="open_issues">Open issues</option>
            <option value="done_issues">Done issues</option>
            <option value="resolved_issues">Resolved issues</option>
            <option value="closed_issues">Closed issues</option>
            <option value="reopened_issues">Reopened issues</option>
            <option value="all">All issues</option>
          </select>
        </div>
        <div class="open-issues-select" v-if="!isProjectDetailsMode">
          <select class="v1-selectbox" id="selectProjectSelectBox" v-model="selectedProject" @change="applyFilters">
            <option value="">Select Project</option>
            <option v-for="project in projects" :key="project.key" :value="project.key">
              {{ project.name }}
            </option>
          </select>
        </div>
        <div class="calendar-drop-down-box" :class="{ 'show-calendar-dropdown': isCalendarOpen }">
          <div class="calendar-drop-down-icon" @click="isCalendarOpen = !isCalendarOpen">
            <img src="@/assets/lems-v1/images/calendar.svg" alt="" />
          </div>
          <div class="calendar-drop-down-content">
            <div class="calendar-drop-down-content-inner">
              <div class="choose-calendar">
                <DatePicker v-model="selectedDate" inline @update:modelValue="validateDate" :max-date="new Date()" />
              </div>
              <div class="custom-filter">
                <button id="allCalendarBtn" @click="setDateFilter('all')" :class="{ active: selectedDateFilter === 'all' }">All</button>
                <button id="lastWeekCalendarBtn" @click="setDateFilter('last_week')" :class="{ active: selectedDateFilter === 'last_week' }">Last Week</button>
                <button id="lastMonthCalendarBtn" @click="setDateFilter('last_month')" :class="{ active: selectedDateFilter === 'last_month' }">Last Month</button>
                <button id="lastYearCalendarBtn" @click="setDateFilter('last_year')" :class="{ active: selectedDateFilter === 'last_year' }">Last Year</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="right-open-issues-list">
      <div class="right-open-issues-row">
        <div v-if="loading" class="loading-data-text">
          <p>Loading issues...</p>
        </div>
        <ul v-else>
          <li v-for="issue in issues" :key="issue.id">
            <router-link :to="`/employee/issue-detail/${issue.key}`" id="openIssuesLink">
              <i>
                <img :src="issue.fields.issuetype.iconUrl" alt="" />
              </i>
              <div class="open-issues-content">
                <strong>{{ issue.key }}</strong>
                <p>{{ issue.fields.summary }}</p>
                <span class="open-issues-time">{{ formatRelativeTime(issue.fields.created) }}</span>
              </div>
            </router-link>
          </li>
        </ul>
      </div>
      <div class="pagination-box" v-if="totalPages > 1">
        <ul>
          <li
            class="pagination-first"
            v-if="currentPage !== 1"
            @click="changePage(currentPage - 1)"
          >
            <button id="paginationPreButton"></button>
          </li>
          <li
            v-for="page in visiblePages"
            :key="page"
            @click="changePage(page)"
            v-if="totalPages != 1"
          >
            <a href="#" :class="{ active: currentPage === page }">{{ page }}</a>
          </li>
          <li
            class="pagination-last"
            v-if="currentPage !== totalPages"
            @click="changePage(currentPage + 1)"
          >
            <button id="paginationNextButton"></button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import constants from "@/utils/en-US-Employee.json";
import axios from "axios";
import { API_BASE_URL } from "@/config/api.config";
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import moment from "moment";

interface Issue {
  id: string;
  key: string;
  summary: string;
  iconUrl: string;
  fields: {
    summary: string;
    issuetype: {
      iconUrl: string;
    };
    created: string;
  };
}

interface Project {
  id: string;
  key: string;
  name: string;
  lead: {
    displayName: string;
    avatarUrls: {
      [key: string]: string;
    };
  };
  avatarUrls: {
    [key: string]: string;
  };
}

export default defineComponent({
  name: "Issues",
  components: {
    DatePicker,
  },
  data() {
    return {
      constants,
      issues: [] as Issue[],
      projects: [] as Project[],
      currentPage: 1,
      totalPages: 0,
      limit: 10,
      start: 0,
      selectedFilter: 'open_issues',
      selectedProject: '',
      selectedDateFilter: 'all',   
      loading: false,
      projectsLoading: false,
      isProjectDetailsMode: false,
      selectedDate: null as Date | null,
      isCalendarOpen: false,
    };
  },
  computed: {
    visiblePages() {
      const maxVisiblePages = 5;
      const halfMaxVisiblePages = Math.floor(maxVisiblePages / 2);
      let start = Math.max(this.currentPage - halfMaxVisiblePages, 1);
      let end = start + maxVisiblePages - 1;

      if (end > this.totalPages) {
        end = this.totalPages;
        start = Math.max(end - maxVisiblePages + 1, 1);
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  mounted() {
    this.isProjectDetailsMode = this.$route.name === 'project-detail';
    
    if (this.isProjectDetailsMode) {
      this.selectedProject = this.$route.params.projectKey as string;
    }
    
    this.getProjects();
    this.getIssues();
  },
  methods: {
    async getProjects() {
      const username = localStorage.getItem("jira_user_username");
      if (!username) return;

      this.projectsLoading = true;
      try {
        const response = await axios.get(`${API_BASE_URL}/jira/projects/${username}/`);
        this.projects = response.data.projects;
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        this.projectsLoading = false;
      }
    },
    async getIssues() {
      const username = localStorage.getItem("jira_user_username");
      if (!username) return;

      this.loading = true;
      try {
        const params = new URLSearchParams({
          start: this.start.toString(),
          limit: this.limit.toString(),
          filter: this.selectedFilter,
          date_filter: this.selectedDateFilter,
        });

        if (this.selectedProject) {
          params.append('project', this.selectedProject);
        }

        if (this.selectedDateFilter === "custom_date" && this.selectedDate) {
          params.append('custom_date', moment(this.selectedDate).format("YYYY-MM-DD"));
        }

        const response = await axios.get(
          `${API_BASE_URL}/jira/issues/${username}/?${params.toString()}`
        );
        this.issues = response.data.issues;
        this.totalPages = response.data.total_pages;
      } catch (error) {
        console.error("Error fetching issues:", error);
        this.issues = [];
        this.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.start = 0;
      this.getIssues();
    },
    setDateFilter(filter: string) {
      this.selectedDateFilter = filter;
      this.applyFilters();
    },
    changePage(page: number) {
      if (page > 0 && page <= this.totalPages) {
        this.currentPage = page;
        this.start = (page - 1) * this.limit;
        this.getIssues();
      }
    },
    formatRelativeTime(createdDate: string): string {
      if (!createdDate) return '';
      
      const now = new Date();
      const created = new Date(createdDate);
      const diffInMs = now.getTime() - created.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      const diffInWeeks = Math.floor(diffInDays / 7);
      const diffInMonths = Math.floor(diffInDays / 30);
      const diffInYears = Math.floor(diffInDays / 365);

      if (diffInMinutes < 1) {
        return 'Just now';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
      } else if (diffInDays < 7) {
        return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
      } else if (diffInWeeks < 4) {
        return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
      } else if (diffInMonths < 12) {
        return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
      } else {
        return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
      }
    },
    validateDate(date: Date) {
      if (date) {
        this.isCalendarOpen = false;
        this.selectedDateFilter = "custom_date";
        this.selectedDate = date;
        this.applyFilters();
      }
      else {
        this.isCalendarOpen = false;
        this.selectedDateFilter = "all"; 
        this.selectedDate = null;
        this.applyFilters(); 
      }
    },
    closeCalendar() {
      this.isCalendarOpen = false;
    },
  },
  watch: {
    '$route.params.projectKey': {
      handler(newKey, oldKey) {
        if (newKey !== oldKey && newKey && this.isProjectDetailsMode) {
          this.selectedProject = newKey as string;
          this.currentPage = 1;
          this.start = 0;
          this.getIssues();
        }
      },
      immediate: false
    }
  }
});
</script>